import { Eye, EyeOff } from "lucide-react";
import { useState } from "react";
import { authTheme } from "../../data/auth/authTheme";
import { loginFormCopy } from "../../data/auth/loginPage";
import { AuthFormDivider } from "./AuthFormDivider";
import { AuthSocialButtons } from "./AuthSocialButtons";
import {
  applyAuthInputBlur,
  applyAuthInputFocus,
  authInputClassName,
  authInputStyle,
} from "./authInputStyles";

export function LoginFormPanel({
  email,
  password,
  loginError,
  launchError,
  isSubmitting,
  onEmailChange,
  onPasswordChange,
  onSubmit,
  onCreateAccount,
  notice,
}) {
  const [showPassword, setShowPassword] = useState(false);

  return (
    <div className="font-inter flex min-h-screen flex-1 items-center justify-center bg-white px-6 py-10 sm:px-12 lg:px-20 xl:px-24">
      <div className="w-full max-w-[460px]">
        <p
          className="mb-8 text-center text-[28px] font-black tracking-[0.12em] lg:hidden"
          style={{ color: authTheme.panelBackground }}
        >
          ITEMA
        </p>

        <div className="mb-10">
          <h1
            className="text-[32px] font-bold leading-tight sm:text-[36px]"
            style={{ color: authTheme.heading }}
          >
            {loginFormCopy.title}
          </h1>
          <p className="mt-3 text-[15px]" style={{ color: authTheme.muted }}>
            {loginFormCopy.accountPrompt}{" "}
            <button
              type="button"
              onClick={onCreateAccount}
              className="font-semibold hover:underline"
              style={{ color: authTheme.accent }}
            >
              {loginFormCopy.createAccountLink}
            </button>
          </p>
        </div>

        {notice ? (
          <p
            className="mb-2 rounded-xl border border-[#bbf7d0] bg-[#f0fdf4] px-4 py-3 text-sm text-[#166534]"
            role="status"
          >
            {notice}
          </p>
        ) : null}

        <form className="flex flex-col gap-6" onSubmit={onSubmit}>
          <label className="flex flex-col gap-2.5">
            <span
              className="text-sm font-semibold"
              style={{ color: authTheme.heading }}
            >
              {loginFormCopy.emailLabel}
            </span>
            <input
              type="email"
              name="email"
              autoComplete="username"
              required
              value={email}
              onChange={onEmailChange}
              className={authInputClassName}
              style={authInputStyle}
              onFocus={applyAuthInputFocus}
              onBlur={applyAuthInputBlur}
              placeholder={loginFormCopy.emailPlaceholder}
            />
          </label>

          <div className="flex flex-col gap-2.5">
            <div className="flex items-center justify-between gap-3">
              <span
                className="text-sm font-semibold"
                style={{ color: authTheme.heading }}
              >
                {loginFormCopy.passwordLabel}
              </span>
              <button
                type="button"
                className="text-sm font-medium hover:underline"
                style={{ color: authTheme.accent }}
              >
                {loginFormCopy.forgotPassword}
              </button>
            </div>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={onPasswordChange}
                className={`${authInputClassName} pr-12`}
                style={authInputStyle}
                onFocus={applyAuthInputFocus}
                onBlur={applyAuthInputBlur}
                placeholder={loginFormCopy.passwordPlaceholder}
              />
              <button
                type="button"
                onClick={() => setShowPassword((value) => !value)}
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-1.5 text-[#6B7280] hover:bg-[#F3F4F6]"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" aria-hidden />
                ) : (
                  <Eye className="h-4 w-4" aria-hidden />
                )}
              </button>
            </div>
          </div>

          {launchError ? (
            <p
              className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
              role="alert"
            >
              {launchError} Open ITEMA again from the TutorCloud teacher sidebar.
            </p>
          ) : null}

          {loginError ? (
            <p
              className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
              role="alert"
            >
              {loginError}
            </p>
          ) : null}

          <button
            type="submit"
            disabled={isSubmitting}
            className="h-[52px] rounded-full text-[15px] font-semibold text-white transition disabled:cursor-not-allowed disabled:bg-primary-disabled disabled:shadow-none"
            style={{
              backgroundColor: authTheme.accent,
              boxShadow: authTheme.accentShadow,
            }}
            onMouseEnter={(event) => {
              if (!isSubmitting) {
                event.currentTarget.style.backgroundColor = authTheme.accentHover;
                event.currentTarget.style.boxShadow = authTheme.accentHoverShadow;
              }
            }}
            onMouseLeave={(event) => {
              event.currentTarget.style.backgroundColor = authTheme.accent;
              event.currentTarget.style.boxShadow = authTheme.accentShadow;
            }}
            onMouseDown={(event) => {
              if (!isSubmitting) {
                event.currentTarget.style.backgroundColor = authTheme.accentActive;
                event.currentTarget.style.boxShadow = authTheme.accentActiveShadow;
              }
            }}
            onMouseUp={(event) => {
              if (!isSubmitting) {
                event.currentTarget.style.backgroundColor = authTheme.accentHover;
                event.currentTarget.style.boxShadow = authTheme.accentHoverShadow;
              }
            }}
          >
            {isSubmitting ? loginFormCopy.submittingLabel : loginFormCopy.submitLabel}
          </button>
        </form>

        <div className="mt-6">
          <AuthSocialButtons />
        </div>

        <AuthFormDivider label={loginFormCopy.orContinueWith} />

        <button
          type="button"
          onClick={onCreateAccount}
          className="h-[52px] w-full rounded-full border-2 bg-white text-[15px] font-semibold"
          style={{
            borderColor: authTheme.accent,
            color: authTheme.accent,
          }}
        >
          {loginFormCopy.createAccount}
        </button>

        <p
          className="mt-10 text-center text-xs leading-6"
          style={{ color: authTheme.muted }}
        >
          {loginFormCopy.footerPrefix}{" "}
          <a
            href="#"
            className="font-medium hover:underline"
            style={{ color: authTheme.accent }}
          >
            {loginFormCopy.termsLabel}
          </a>{" "}
          and{" "}
          <a
            href="#"
            className="font-medium hover:underline"
            style={{ color: authTheme.accent }}
          >
            {loginFormCopy.privacyLabel}
          </a>
          .
        </p>
      </div>
    </div>
  );
}
